import { Trans, useLingui } from '@lingui/react/macro';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { Big } from 'big.js';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'ui-kit';
import { TokenInputField } from '@/components/input_field/token_input_field.tsx';
import { useTokenFormatter } from '@/hooks/useTokenFormatter';
import { bigIntToBig, bigToString } from '@/utils/bigUtils';
import { decimalStringToBigInt, type TokenNumberFormatOptions } from '@/utils/tokenNumberUtils.ts';
import { useTokenAmountValidation } from '../../../hooks/useTokenAmountValidation.ts';
import { FunCheckoutButton } from '../../../providers/fun_bridge/fun_bridge.tsx';
import useAppStore from '../../../store/rootStore.ts';
import { useDepositProcess } from '../hooks/useDepositProcess';

interface DepositPanelProps {
  isActive: boolean;
  onClose?: () => void;
  autoFocus?: boolean;
}

export function DepositPanel({ isActive, onClose, autoFocus = true }: DepositPanelProps) {
  const { t } = useLingui();
  const {
    startDepositCollateral,
    resetProcess,
    isPending,
    isSuccess,
    requestApprovedAmount,
    token,
    minDeposit,
    maxAmount,
    maxEquity,
    isMaxEquityLimited,
  } = useDepositProcess({ isDialogOpen: isActive });

  const handleBridgeSuccess = useCallback(() => {
    setTimeout(() => {
      requestApprovedAmount();
    }, 600);
  }, [requestApprovedAmount]);

  const isAuthenticated = useAppStore((state) => state.isAtLeastAuthenticated());
  const isDelegated = useAppStore((state) => state.isDelegated);
  const funBridgeEnabled = useAppStore((state) => !!state.config?.features?.funBridgeEnabled);

  const formatToken = useTokenFormatter(token);
  const tokenSymbol = token?.symbol || '';
  const [approveAmountInput, setApproveAmountInput] = useState('');

  const displayOptions: Partial<TokenNumberFormatOptions> = useMemo(() => ({ minDecimals: 0 }), []);

  const formattedMinDeposit = useMemo(() => {
    return formatToken(token && minDeposit != null ? minDeposit : 0n, displayOptions);
  }, [minDeposit, token, formatToken, displayOptions]);

  const formattedMaxAmount = useMemo(() => {
    if (!token || maxAmount?.value == null) return formatToken(0n, displayOptions);
    const dp = Math.min(token.display_precision ?? 2, maxAmount.decimals);
    const maxBig = bigIntToBig(maxAmount.value, maxAmount.decimals);
    const truncatedBigInt = decimalStringToBigInt(bigToString(maxBig, dp, Big.roundDown), token.decimals);
    return formatToken(truncatedBigInt, displayOptions);
  }, [maxAmount, token, formatToken, displayOptions]);

  const formattedMaxEquity = useMemo(() => {
    return formatToken(token && maxEquity != null ? maxEquity : 0n, displayOptions);
  }, [maxEquity, token, formatToken, displayOptions]);

  // Wallet holds more than the exchange allows and there's no headroom left — deposits are fully blocked.
  const isAtEquityLimit = isMaxEquityLimited && (maxAmount?.value ?? 0n) <= 0n;

  const { error: inputError, isValid } = useTokenAmountValidation({
    inputValue: approveAmountInput,
    token,
    minAmount: minDeposit,
    maxAmount,
    formattedMinAmount: formattedMinDeposit,
  });

  // When the equity cap is what's binding, "exceeds wallet balance" is wrong — the wallet may hold more.
  const inputErrorMessage =
    inputError.type === 'MAX_AMOUNT' && isMaxEquityLimited ? t`Amount exceeds your deposit limit.` : inputError.message;

  const handleClose = useCallback(() => {
    resetProcess();
    onClose?.();
  }, [resetProcess, onClose]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);

  useEffect(() => {
    if (isActive) {
      if (!isAuthenticated) {
        handleClose();
      } else {
        setApproveAmountInput('');
        requestApprovedAmount();
      }
    }
  }, [isActive, isAuthenticated, requestApprovedAmount, handleClose]);

  const handleSetMax = () => {
    if (maxAmount && token) {
      const dp = Math.min(token.display_precision ?? 2, maxAmount.decimals);
      const maxBig = bigIntToBig(maxAmount.value, maxAmount.decimals);
      setApproveAmountInput(bigToString(maxBig, dp, Big.roundDown));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !isValid || approveAmountInput.trim() === '') return;

    const approveAmount = decimalStringToBigInt(approveAmountInput, token.decimals);
    startDepositCollateral({ approveAmount });
  };

  const getButtonText = () => {
    if (isPending) {
      return t`Processing...`;
    }
    if (!isValid && inputError.type === 'MAX_AMOUNT') {
      return t`Try lower amount`;
    }
    return t({
      comment:
        'Deposit panel submit button. An action ("add funds"), not the noun for a deposit transaction — the uncontexted "Deposit" is the noun used by the account-history row and the table filter. Sits beside a "Withdraw" button, so keep the two the same part of speech.',
      context: 'action',
      message: 'Deposit',
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        <Trans>
          Deposit {tokenSymbol} to use as collateral. Minimum: {formattedMinDeposit}.
        </Trans>
        {isDelegated ? (
          <>
            <br />
            <Trans>Max balance to use is checked on delegated account smart contract, not connected wallet.</Trans>
          </>
        ) : null}
      </Typography>
      <Box sx={{ '& .MuiOutlinedInput-input': { pl: 0 } }}>
        <TokenInputField
          token={token}
          value={approveAmountInput}
          onChange={setApproveAmountInput}
          onMaxClick={handleSetMax}
          disabled={isPending}
          maxAmount={maxAmount}
          errorMessage={inputErrorMessage}
          formattedMaxAmount={formattedMaxAmount}
          inputId="depositAmount"
          autoFocus={autoFocus}
        />
      </Box>
      {isMaxEquityLimited ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>
            {isAtEquityLimit
              ? t`You've reached your ${formattedMaxEquity} ${tokenSymbol} equity limit`
              : t`You can deposit up to ${formattedMaxAmount} ${tokenSymbol}`}
          </AlertTitle>
          <Trans>
            Equity is your balance plus open positions, and is capped at {formattedMaxEquity} {tokenSymbol}.
          </Trans>
        </Alert>
      ) : null}
      <Box display="flex" gap={2} width="100%">
        {funBridgeEnabled ? (
          <FunCheckoutButton fullWidth variant="outlined" size="large" source="deposit_panel" onSuccess={handleBridgeSuccess}>
            <Trans>Bridge AUSD</Trans>
          </FunCheckoutButton>
        ) : null}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isPending || !isValid}
          disableElevation={false}
        >
          {getButtonText()}
        </Button>
      </Box>
    </Box>
  );
}
